import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, Pressable, Alert, Platform } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles } from '../../shared/utils/styles';
import { DesignTokens } from '../../shared/types/tokens';
import { useResponsive } from '../../shared/utils/responsive';
import { CommentContextType, Comment as CommentType } from '../../shared/types/comments';
import { useCommentStore } from '../../store/commentStore';
import { CommentItem } from './CommentItem';
import { CommentInput } from './CommentInput';
import { Button } from '../ui/Button';
import { decodeHtmlEntities } from '../../shared/utils/html-entities';
import { eventEmitter, THREAD_EVENTS } from '../../shared/utils/eventEmitter';
import { logger } from '../../shared/utils/logger';

interface CommentThreadProps {
  contextType: CommentContextType;
  contextId: number;
  threadId?: number | null; // If provided, show specific thread; otherwise show thread list
  autoCreateThread?: boolean; // Auto-create a default thread if none exists
  defaultThreadTitle?: string;
}

export function CommentThread({
  contextType,
  contextId,
  threadId,
  autoCreateThread = false,
  defaultThreadTitle = 'General Discussion',
}: CommentThreadProps) {
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const responsive = useResponsive();
  const { isMobile, isSmallMobile, isTablet, isDesktop } = responsive;
  const styles = makeStyles(tokens, utils, { isMobile, isSmallMobile, isTablet, isDesktop });
  
  const {
    threads,
    comments,
    threadDetails,
    loadingThreads,
    loadingComments,
    creatingThread,
    creatingComment,
    loadThreads,
    loadThread,
    createThread,
    createComment,
    startPolling,
    stopPolling,
    markThreadRead,
  } = useCommentStore();

  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyingToAuthor, setReplyingToAuthor] = useState<string | null>(null);
  const [selectedThreadId, setSelectedThreadId] = useState<number | null>(threadId || null);
  const scrollViewRef = useRef<ScrollView>(null);
  const commentInputRef = useRef<View>(null);
  const previousCommentsLengthRef = useRef<number>(0);
  const userJustPostedRef = useRef<boolean>(false);

  const contextKey = `${contextType}:${contextId}`;
  const contextThreads = threads[contextKey] || [];
  const isLoadingThreads = loadingThreads[contextKey] || false;

  // Determine which thread to display - use state to maintain stability
  const displayThreadId = threadId || selectedThreadId || (contextThreads.length > 0 ? contextThreads[0].id : null);
  const displayThread = displayThreadId ? threadDetails[displayThreadId] : null;
  const displayComments: CommentType[] = displayThreadId ? comments[displayThreadId] || [] : [];
  const isLoadingComments = displayThreadId ? loadingComments[displayThreadId] || false : false;
  const isCreating = displayThreadId ? creatingComment[displayThreadId] || false : false;

  // Scroll to bottom when comments are added or updated
  const scrollToBottom = useCallback((animated = true) => {
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated });
    }, 100);
  }, []);

  // Auto-scroll to bottom when new comments arrive
  useEffect(() => {
    const currentLength = displayComments.length;
    if (currentLength > previousCommentsLengthRef.current && !isLoadingComments) {
      scrollToBottom(true);
    }
    previousCommentsLengthRef.current = currentLength;
  }, [displayComments.length, isLoadingComments, scrollToBottom]);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (!isLoadingComments && displayComments.length > 0) {
      scrollToBottom(false);
    }
  }, [isLoadingComments, displayComments.length, scrollToBottom]);

  // Load threads on mount
  useEffect(() => {
    loadThreads(contextType, contextId);
  }, [contextType, contextId]);

  // Update selected thread when threads are loaded (only if not already set)
  useEffect(() => {
    if (contextThreads.length > 0 && !selectedThreadId && !threadId) {
      setSelectedThreadId(contextThreads[0].id);
    }
  }, [contextThreads.length, selectedThreadId, threadId]);

  // Track which threads we've already marked as read to prevent duplicate POST requests
  const markedAsReadRef = useRef<Set<number>>(new Set());

  // Load specific thread if threadId provided or selected
  useEffect(() => {
    if (displayThreadId) {
      loadThread(displayThreadId);
      // Only mark as read once per session to prevent redundant POST requests
      if (!markedAsReadRef.current.has(displayThreadId)) {
        markedAsReadRef.current.add(displayThreadId);
        markThreadRead(displayThreadId);
      }
    }
  }, [displayThreadId]);

  // Scroll to comment input when threadId is provided (e.g., when navigating from discussions)
  useEffect(() => {
    if (threadId && !isLoadingComments && displayThreadId && displayThread) {
      // Delay to ensure everything is rendered
      const timer = setTimeout(() => {
        // First scroll the comments list to bottom
        scrollToBottom(true);
        
        // Then scroll the page to the comment input
        setTimeout(() => {
          if (Platform.OS === 'web') {
            // Try to find the comment input by threadId first (from URL)
            const inputElementById = threadId ? document.getElementById(`comment-input-${threadId}`) : null;
            if (inputElementById) {
              const elementTop = inputElementById.getBoundingClientRect().top + window.pageYOffset;
              window.scrollTo({ top: elementTop - 150, behavior: 'smooth' });
              return;
            }
            
            // Fallback: try with displayThreadId
            if (displayThreadId) {
              const inputElement = document.getElementById(`comment-input-${displayThreadId}`);
              if (inputElement) {
                const elementTop = inputElement.getBoundingClientRect().top + window.pageYOffset;
                window.scrollTo({ top: elementTop - 150, behavior: 'smooth' });
                return;
              }
            }
            
            // Last fallback: try to find by data attribute
            const fallbackInput = document.querySelector('[data-comment-input="true"]') as HTMLElement;
            if (fallbackInput) {
              const elementTop = fallbackInput.getBoundingClientRect().top + window.pageYOffset;
              window.scrollTo({ top: elementTop - 150, behavior: 'smooth' });
            }
          } else {
            // Native: scroll the comments list to bottom
            scrollToBottom(true);
          }
        }, 400);
      }, 800);
      
      return () => clearTimeout(timer);
    }
  }, [threadId, isLoadingComments, displayThreadId, displayThread, scrollToBottom]);

  // Start polling for real-time updates
  useEffect(() => {
    startPolling(contextType, contextId);
    return () => {
      stopPolling(contextType, contextId);
    };
  }, [contextType, contextId]);

  // Don't auto-create threads - only create when user posts first comment
  // This prevents empty threads from cluttering the discussions tab

  const handleCreateComment = async (content: string) => {
    // If no thread exists, create one first (only when user actually posts a comment)
    let threadIdToUse = displayThreadId;
    
    if (!threadIdToUse && autoCreateThread) {
      try {
        const newThread = await createThread({
          context_type: contextType,
          context_id: contextId,
          thread_title: defaultThreadTitle,
        });
        
        if (newThread) {
          threadIdToUse = newThread.id;
          setSelectedThreadId(newThread.id);
          // Emit event to notify other components (like discussions tab)
          eventEmitter.emit(THREAD_EVENTS.CREATED, newThread);
        } else {
          throw new Error('Failed to create thread');
        }
      } catch (error: any) {
        logger.error('Error creating thread', error, 'comments');
        Alert.alert(
          'Error',
          error?.message || 'Failed to create discussion thread. Please try again.'
        );
        return;
      }
    }
    
    if (!threadIdToUse) {
      Alert.alert('Error', 'No discussion thread available. Please start a discussion first.');
      return;
    }

    try {
      // Stop polling temporarily to avoid race conditions
      stopPolling(contextType, contextId);
      
      // Mark that user is posting (for auto-scroll)
      userJustPostedRef.current = true;
      
      await createComment({
        thread_id: threadIdToUse,
        parent_comment_id: replyingTo,
        content,
      });

      setReplyingTo(null);
      setReplyingToAuthor(null);
      
      // Scroll to bottom immediately (optimistic comment is already shown)
      scrollToBottom(true);
      
      // Get the updated thread from the store and emit event
      const storeState = useCommentStore.getState();
      const updatedThread = storeState.threadDetails[threadIdToUse];
      if (updatedThread) {
        eventEmitter.emit(THREAD_EVENTS.COMMENT_ADDED, updatedThread);
      }
      
      // Restart polling after a short delay
      setTimeout(() => {
        startPolling(contextType, contextId);
      }, 2000);
    } catch (error: any) {
      logger.error('Error creating comment', error, 'comments');
      // Restart polling even on error
      startPolling(contextType, contextId);
      // Show error to user
      Alert.alert(
        'Error',
        error?.message || 'Failed to create comment. Please try again.'
      );
    }
  };

  const handleReply = (parentId: number, authorEmail: string) => {
    setReplyingTo(parentId);
    setReplyingToAuthor(authorEmail);
  };

  // Build comment tree from flat list
  const buildCommentTree = (flatComments: CommentType[]) => {
    type CommentWithReplies = CommentType & { replies: CommentType[] };
    const commentMap = new Map<number, CommentWithReplies>(
      flatComments.map((c) => [c.id, { ...c, replies: [] }])
    );
    const rootComments: CommentWithReplies[] = [];

    flatComments.forEach((comment) => {
      const commentWithReplies = commentMap.get(comment.id)!;
      if (comment.parent_comment_id) {
        const parent = commentMap.get(comment.parent_comment_id);
        if (parent) {
          parent.replies = parent.replies || [];
          parent.replies.push(commentWithReplies);
        }
      } else {
        rootComments.push(commentWithReplies);
      }
    });

    return rootComments;
  };

  const commentTree = buildCommentTree(displayComments);

  if (isLoadingThreads && contextThreads.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={tokens.color.primary} />
        <Text style={styles.loadingText}>Loading comments...</Text>
      </View>
    );
  }

  if (!displayThreadId) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>No discussion yet</Text>
        <Text style={styles.emptySubtext}>
          {autoCreateThread 
            ? 'Start the conversation by posting a comment below'
            : 'Start a discussion to begin the conversation'}
        </Text>
        {/* Show comment input even when no thread exists - thread will be created on first comment */}
        {autoCreateThread && (
          <View style={styles.commentInputContainer}>
            <CommentInput
              onSubmit={handleCreateComment}
              placeholder="Start the discussion..."
              submitLabel={creatingThread ? 'Creating...' : 'Post'}
            />
          </View>
        )}
      </View>
    );
  }

  const thread = displayThread || contextThreads.find((t) => t.id === displayThreadId);

  return (
    <View style={styles.container}>
      {thread && (
        <View style={styles.threadHeader}>
          <Text style={styles.threadTitle}>
            {decodeHtmlEntities(thread.thread_title || 'Discussion')}
          </Text>
          {thread.unread_count && thread.unread_count > 0 && (
            <View style={styles.unreadBadge}>
              <Text style={styles.unreadText}>{thread.unread_count}</Text>
            </View>
          )}
        </View>
      )}

      {isLoadingComments ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={tokens.color.primary} />
        </View>
      ) : (
        <ScrollView 
          ref={scrollViewRef}
          style={styles.commentsContainer}
          onContentSizeChange={() => {
            // Only auto-scroll when user just posted (not during polling)
            if (userJustPostedRef.current) {
              scrollToBottom(true);
              userJustPostedRef.current = false;
            }
          }}
        >
          {commentTree.length === 0 ? (
            <Text style={styles.emptyCommentsText}>No comments yet. Be the first to comment!</Text>
          ) : (
            commentTree.map((comment, index) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                index={index}
                isLastInGroup={index === commentTree.length - 1}
                onReply={(parentId) => {
                  const parentComment = displayComments.find((c) => c.id === parentId);
                  handleReply(parentId, parentComment?.created_by_user?.email || 'Unknown');
                }}
              />
            ))
          )}
        </ScrollView>
      )}

      {thread?.is_locked ? (
        <View style={styles.lockedContainer}>
          <Text style={styles.lockedText}>This thread is locked</Text>
        </View>
      ) : displayThreadId ? (
        <>
          {replyingTo && (
            <View style={styles.replyingContainer}>
              <Text style={styles.replyingText}>
                Replying to {replyingToAuthor}
              </Text>
              <Pressable onPress={() => {
                setReplyingTo(null);
                setReplyingToAuthor(null);
              }}>
                <Text style={styles.cancelReplyText}>Cancel</Text>
              </Pressable>
            </View>
          )}
          <CommentInput
            ref={commentInputRef}
            onSubmit={handleCreateComment}
            placeholder={replyingTo ? 'Write a reply...' : 'Write a comment...'}
            submitLabel={isCreating ? 'Posting...' : 'Post'}
            parentAuthor={replyingToAuthor || undefined}
            onCancel={replyingTo ? () => {
              setReplyingTo(null);
              setReplyingToAuthor(null);
            } : undefined}
            inputId={threadId ? `comment-input-${threadId}` : (displayThreadId ? `comment-input-${displayThreadId}` : undefined)}
          />
        </>
      ) : null}
    </View>
  );
}

function makeStyles(
  tokens: DesignTokens,
  utils: ReturnType<typeof createStyles>,
  responsive: { isMobile: boolean; isTablet: boolean; isDesktop: boolean; isSmallMobile?: boolean }
) {
  const { isMobile, isTablet, isDesktop } = responsive;
  const isSmallMobile = responsive.isSmallMobile ?? false;
  
  return StyleSheet.create({
    container: {
      flex: 1,
      maxWidth: isDesktop ? 1200 : '100%',
      alignSelf: 'center',
      width: '100%',
    },
    threadHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: isSmallMobile ? utils.spacing.sm : isMobile ? utils.spacing.md : utils.spacing.lg,
      paddingHorizontal: isDesktop ? utils.spacing.xl : isSmallMobile ? utils.spacing.sm : utils.spacing.md,
      backgroundColor: tokens.color.surface,
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
      gap: utils.spacing.sm,
    },
    threadTitle: {
      fontSize: isSmallMobile ? utils.typography.sizes.base : utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      flex: 1,
      flexWrap: 'wrap',
    },
    unreadBadge: {
      backgroundColor: tokens.color.primary,
      borderRadius: utils.radius.full,
      paddingHorizontal: isSmallMobile ? utils.spacing.xs : utils.spacing.sm,
      paddingVertical: isSmallMobile ? 2 : utils.spacing.xs,
      minWidth: isSmallMobile ? 20 : 24,
      alignItems: 'center',
      flexShrink: 0,
    },
    unreadText: {
      color: tokens.color.onPrimary,
      fontSize: isSmallMobile ? 10 : utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.bold,
      fontFamily: utils.typography.body.fontFamily,
    },
    commentsContainer: {
      flex: 1,
      paddingVertical: isSmallMobile ? utils.spacing.xs : utils.spacing.sm,
      paddingHorizontal: 0,
      minHeight: isSmallMobile ? 250 : isMobile ? 350 : 400,
      maxHeight: isSmallMobile ? 400 : isMobile ? 500 : isTablet ? 600 : 700,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: isSmallMobile ? utils.spacing.lg : utils.spacing.xl,
    },
    loadingText: {
      marginTop: utils.spacing.md,
      fontSize: isSmallMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    emptyContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: isSmallMobile ? utils.spacing.lg : utils.spacing.xl,
    },
    emptyText: {
      fontSize: isSmallMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
      color: `${tokens.color.onSurface}99`,
      marginBottom: utils.spacing.sm,
      fontFamily: utils.typography.body.fontFamily,
    },
    emptySubtext: {
      fontSize: isSmallMobile ? utils.typography.sizes.xs : utils.typography.sizes.sm,
      color: `${tokens.color.onSurface}66`,
      marginBottom: utils.spacing.lg,
      textAlign: 'center',
      fontFamily: utils.typography.body.fontFamily,
      paddingHorizontal: isSmallMobile ? utils.spacing.sm : 0,
    },
    commentInputContainer: {
      width: '100%',
      maxWidth: isDesktop ? 800 : '100%',
      paddingHorizontal: isDesktop ? utils.spacing.xl : (isSmallMobile ? utils.spacing.sm : isMobile ? utils.spacing.md : utils.spacing.lg),
    },
    emptyCommentsText: {
      fontSize: isSmallMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
      color: `${tokens.color.onSurface}66`,
      textAlign: 'center',
      padding: isSmallMobile ? utils.spacing.lg : utils.spacing['2xl'],
      fontFamily: utils.typography.body.fontFamily,
      fontStyle: 'italic',
    },
    createButton: {
      marginTop: utils.spacing.md,
    },
    lockedContainer: {
      padding: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
      backgroundColor: `${tokens.color.error}22`,
      borderRadius: utils.radius.md,
      margin: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
    },
    lockedText: {
      fontSize: isSmallMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
      color: tokens.color.error,
      textAlign: 'center',
      fontFamily: utils.typography.body.fontFamily,
    },
    replyingContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: isSmallMobile ? utils.spacing.xs : utils.spacing.sm,
      backgroundColor: `${tokens.color.primary}22`,
      marginHorizontal: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
      marginTop: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
      borderRadius: utils.radius.sm,
    },
    replyingText: {
      fontSize: isSmallMobile ? utils.typography.sizes.xs : utils.typography.sizes.sm,
      color: tokens.color.primary,
      fontFamily: utils.typography.body.fontFamily,
      flex: 1,
    },
    cancelReplyText: {
      fontSize: isSmallMobile ? utils.typography.sizes.xs : utils.typography.sizes.sm,
      color: tokens.color.primary,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
  });
}
